/**
 * SheetsReaderPicker - Picker de planilha + aba para sheets_reader.
 *
 * Fluxo:
 * 1. Google Picker para selecionar planilha
 * 2. API carrega abas da planilha selecionada
 * 3. Usuário seleciona aba via dropdown
 */

import { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { Alert, Spinner } from 'react-bootstrap';
import { IconAlertCircle, IconFileSpreadsheet, IconExternalLink, IconX } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { api } from '../../../../api/Middleware';
import { API_URL } from '../../../../api/Urls';
import { useGooglePicker, SHEETS_MIME_TYPE } from '../../hooks/useGooglePicker';

const SheetsReaderPicker = ({ agentId, integrations, config, onChange }) => {
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [tabs, setTabs] = useState([]);

  const selectedSpreadsheetId = config?.spreadsheet_id || '';
  const selectedSpreadsheetName = config?.spreadsheet_name || '';
  const selectedSheetName = config?.sheet_name || '';

  const gsIntegration = (integrations || []).find(
    (i) => (i.integration_type === 'google_sheets' || i.integration_type === 'google_workspace') && i.is_active
  );
  const integrationId = gsIntegration?.id;

  // Google Picker for spreadsheet selection
  const { openPicker } = useGooglePicker({
    agentId,
    integrationId,
    integrationType: gsIntegration?.integration_type || 'google_sheets',
    mimeTypes: SHEETS_MIME_TYPE,
    multiSelect: false,
    onSelect: (files) => {
      if (files.length > 0) {
        const file = files[0];
        onChange('spreadsheet_id', file.id);
        onChange('spreadsheet_name', file.name);
        onChange('sheet_name', '');
        setTabs([]);
        fetchTabs(file.id);
      }
    },
    onError: (msg) => {
      Swal.fire({ icon: 'error', title: 'Erro', text: msg });
    },
  });

  // Fetch tabs for selected spreadsheet
  const fetchTabs = useCallback(async (spreadsheetId) => {
    if (!agentId || !integrationId || !spreadsheetId) {
      setTabs([]);
      return;
    }
    setLoadingTabs(true);
    try {
      const response = await api.get(
        `${API_URL}agents_flow/agents/${agentId}/integrations/${integrationId}/google-sheets/${spreadsheetId}/tabs/`
      );
      setTabs(response.data.sheets || []);
    } catch (err) {
      console.error('Error fetching tabs:', err);
      setTabs([]);
    } finally {
      setLoadingTabs(false);
    }
  }, [agentId, integrationId]);

  // Load tabs when spreadsheet is already selected
  useEffect(() => {
    if (selectedSpreadsheetId && integrationId) {
      fetchTabs(selectedSpreadsheetId);
    }
  }, [selectedSpreadsheetId, integrationId, fetchTabs]);

  const handleClear = () => {
    onChange('spreadsheet_id', '');
    onChange('spreadsheet_name', '');
    onChange('sheet_name', '');
    setTabs([]);
  };

  if (!gsIntegration) {
    return (
      <Alert variant="warning" className="mt-2 mb-3 d-flex align-items-center gap-2">
        <IconAlertCircle size={18} />
        <small>Configure a integração Google Sheets/Workspace na aba <strong>Integrações</strong>.</small>
      </Alert>
    );
  }

  const tabOptions = tabs.map((t) => ({
    value: t.title,
    label: `${t.title} (${t.row_count} linhas, ${t.column_count} colunas)`,
    headers: t.headers,
  }));

  const selectedTab = tabOptions.find((o) => o.value === selectedSheetName) || null;

  const handleTabChange = (option) => {
    onChange('sheet_name', option?.value || '');
  };

  return (
    <div className="mb-3 d-flex flex-column gap-3">
      {/* Spreadsheet selector via Picker */}
      <div>
        <label className="form-label mb-1 small">Planilha</label>

        {selectedSpreadsheetId ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
            border: '1px solid #dee2e6', borderRadius: '8px', background: '#f9fafb',
          }}>
            <IconFileSpreadsheet size={18} style={{ color: '#0f9d58', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedSpreadsheetName || selectedSpreadsheetId}
            </span>
            <button
              onClick={openPicker}
              style={{
                background: 'none', border: '1px solid #d1d5db', borderRadius: '6px',
                padding: '4px 10px', cursor: 'pointer', fontSize: '12px', color: '#4b5563',
              }}
            >
              Trocar
            </button>
            <button
              onClick={handleClear}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
              title="Remover seleção"
            >
              <IconX size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={openPicker}
            style={{
              width: '100%', padding: '12px', border: '2px dashed #d1d5db', borderRadius: '8px',
              background: '#fafafa', cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px', fontSize: '13px', fontWeight: 500, color: '#4b5563',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0f9d58'; e.currentTarget.style.background = '#f0fdf4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = '#fafafa'; }}
          >
            <IconFileSpreadsheet size={16} style={{ color: '#0f9d58' }} />
            Selecionar planilha do Google
          </button>
        )}
      </div>

      {/* Tab selector (from API, not Picker) */}
      {selectedSpreadsheetId && (
        <div>
          <label className="form-label small">Aba</label>
          {loadingTabs ? (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
              <span className="ms-2 text-muted small">Carregando abas...</span>
            </div>
          ) : (
            <>
              <Select
                options={tabOptions}
                value={selectedTab}
                onChange={handleTabChange}
                placeholder="Selecione a aba..."
                noOptionsMessage={() => 'Nenhuma aba encontrada'}
                isSearchable
                maxMenuHeight={200}
              />
              {selectedTab?.headers && selectedTab.headers.length > 0 && (
                <small className="text-muted d-block mt-1">
                  Colunas: {selectedTab.headers.join(', ')}
                </small>
              )}
            </>
          )}
        </div>
      )}

      {/* Link to open spreadsheet */}
      {selectedSpreadsheetId && (
        <a
          href={`https://docs.google.com/spreadsheets/d/${selectedSpreadsheetId}/edit`}
          target="_blank"
          rel="noopener noreferrer"
          className="small text-decoration-none d-inline-flex align-items-center gap-1"
        >
          <IconExternalLink size={14} />
          Abrir planilha no Google Sheets
        </a>
      )}
    </div>
  );
};

export default SheetsReaderPicker;
